/**
 * @fileoverview Public landing page served at `/`.
 *
 * Marketing surface for first-time visitors. Replaces the old behaviour
 * where `www.futureprotea.com` opened straight onto the admin login form,
 * which was opaque to anyone who wasn't already a platform admin.
 *
 * Logged-in admins are redirected to the dashboard so they don't see the
 * marketing page on every visit. Anonymous visitors land here and can:
 *   - Read what the platform does (hero + features + workflow).
 *   - Download the latest mobile APK from /apk/app-release.apk (the same
 *     file the deploy pipeline updates on every release).
 *   - Click "Admin Login" to reach the gated admin panel at /login.
 *
 * This file is intentionally thin — each on-page section lives under
 * `./sections/` and shared data/types live in `./constants.ts`. Splitting
 * the original 800-line monolith made each section ownable and keeps
 * this entry around 60 lines.
 */
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { SiteHeader } from './sections/SiteHeader'
import { Hero } from './sections/Hero'
import { StatBar } from './sections/StatBar'
import { FeaturesSection } from './sections/FeaturesSection'
import { MobileShowcase } from './sections/MobileShowcase'
import { HowItWorks } from './sections/HowItWorks'
import { DownloadCta } from './sections/DownloadCta'
import { SiteFooter } from './sections/SiteFooter'

export function LandingPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useAuth()

  // Already-signed-in admins skip the marketing page on every visit and
  // go straight to where they were headed (the dashboard). We wait for
  // the auth check to settle so the page doesn't flash for un-auth users.
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-hidden">
      {/* Page-wide ambient background — mirrors the dashboard
          `GradientStatCard` recipe at full-viewport scale.
          • Bottom-to-top `--primary` wash (`from-primary/[0.08]
            via-primary/[0.02] to-transparent`) — pools the active
            palette at the bottom and fades upward, exactly like the
            stat cards. Re-tints automatically when the user picks a
            different accent in the palette drawer because Tailwind's
            `bg-primary/N` compiles to `color-mix(in oklch, var(--primary)
            N%, transparent)`.
          • Two soft `blur-3xl` blobs in the corners (`primary` top-right,
            `amber` bottom-left) give the wash a hint of depth without
            distracting from the foreground.
          • `dark:` opacity stops are bumped a notch (0.12 / 0.04) so the
            tint stays readable on the deep card token.
          Pinned with `fixed inset-0 -z-10 pointer-events-none` so it
          never intercepts clicks / scroll on the foreground content. */}
      <div aria-hidden className="fixed inset-0 -z-10 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/[0.08] via-primary/[0.02] to-transparent dark:from-primary/[0.12] dark:via-primary/[0.04] dark:to-transparent" />
        <div className="absolute -top-40 -right-40 h-[28rem] w-[28rem] rounded-full bg-primary/15 blur-3xl dark:bg-primary/10" />
        <div className="absolute -bottom-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-500/10" />
      </div>

      <SiteHeader />

      <main>
        <Hero />
        <StatBar />
        <FeaturesSection />
        <MobileShowcase />
        <HowItWorks />
        <DownloadCta />
      </main>

      <SiteFooter />
    </div>
  )
}
